import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, User as UserIcon, Ticket as TicketIcon } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending_approval: 'Pending',
  access_granted: 'Granted',
  used: 'Used',
  revoked: 'Revoked',
};

const STATUS_CLASS = {
  pending_approval: 'text-[#FFB800] border-[#FFB800]/30 bg-[#FFB800]/10',
  access_granted: 'text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10',
  used: 'text-[#6EE7B7] border-[#6EE7B7]/30 bg-[#6EE7B7]/10',
  revoked: 'text-[#C44545] border-[#C44545]/30 bg-[#C44545]/10',
};

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso.slice(0, 16); }
}

export default function GuestDetailView() {
  const { email } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem('session_token');
      const qs = new URLSearchParams();
      qs.set('email', email);
      ['from', 'to'].forEach(k => { if (search.get(k)) qs.set(k, search.get(k)); });
      const res = await axios.get(`${API}/reports/guest-detail?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load guest detail');
    } finally { setLoading(false); }
  }, [email, search]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary;

  return (
    <div className="min-h-screen bg-[#050505] text-[#EAE7E1]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-[#AFAFAF] hover:text-[#D4AF37] h-9 px-2"
            data-testid="guest-detail-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <UserIcon className="w-5 h-5 text-[#D4AF37]" />
          <h1 className="text-xl font-semibold tracking-tight truncate" data-testid="guest-detail-title">
            {decodeURIComponent(email)}
          </h1>
        </div>

        {error && (
          <div className="bg-[#C44545]/10 border border-[#C44545]/30 rounded-sm p-4 mb-6 text-sm text-[#C44545]" data-testid="guest-detail-error">
            {error}
          </div>
        )}

        {/* KPI block */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="guest-detail-kpis">
            <Kpi label="Events Attended" value={summary.total_events_attended} testId="gd-kpi-events" />
            <Kpi label="Total Passes" value={summary.total_passes} testId="gd-kpi-passes" />
            <Kpi label="Total Attendees" value={summary.total_attendees} testId="gd-kpi-attendees" />
            <Kpi label="Checked-In Count" value={summary.checked_in_count} testId="gd-kpi-checkin" />
          </div>
        )}

        {/* Ticket history table */}
        <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm overflow-x-auto" data-testid="guest-detail-table">
          <table className="min-w-full text-sm">
            <thead className="border-b border-[#1A1A1A]">
              <tr className="text-left">
                <Th>Event</Th>
                <Th>Tier</Th>
                <Th className="text-right">Attendees</Th>
                <Th>Status</Th>
                <Th>Issued</Th>
                <Th>Checked-In</Th>
                <Th>Revoked</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-[#555]" data-testid="guest-detail-loading">Loading…</td></tr>
              )}
              {!loading && (data?.tickets || []).length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-[#555]" data-testid="guest-detail-empty">
                  <TicketIcon className="w-6 h-6 mx-auto mb-2 text-[#333]" />
                  No ticket history for this guest in the current scope
                </td></tr>
              )}
              {!loading && (data?.tickets || []).map(t => (
                <tr
                  key={t.ticket_id}
                  className="border-b border-[#111] hover:bg-[#0D0D0D] transition-colors"
                  data-testid={`guest-detail-row-${t.ticket_id}`}
                >
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/reports/event/${t.event_id}`)}
                      className="text-[#C6A66B] hover:text-[#D4AF37] underline-offset-2 hover:underline text-left"
                      data-testid={`guest-detail-event-${t.ticket_id}`}
                    >
                      {t.event_name}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-[#AFAFAF]">{t.tier_name || '—'}</td>
                  <td className="px-4 py-3 text-right text-[#EAE7E1]">{t.attendees_count}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase border rounded ${STATUS_CLASS[t.status] || ''}`}>
                      {STATUS_LABEL[t.status] || t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#777] text-xs whitespace-nowrap">{fmt(t.issued_at)}</td>
                  <td className="px-4 py-3 text-[#777] text-xs whitespace-nowrap">{fmt(t.scanned_at)}</td>
                  <td className="px-4 py-3 text-[#777] text-xs whitespace-nowrap">{fmt(t.revoked_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, testId }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-1 text-[#D4AF37]">{value}</div>
    </div>
  );
}

function Th({ children, className = '' }) {
  return (
    <th className={`px-4 py-3 text-[10px] uppercase tracking-wider text-[#777] font-semibold ${className}`}>{children}</th>
  );
}
